import React, { useState, useEffect, useContext } from 'react';
import styled from 'astroturf/react';
import { Job, CostQuota } from './types';
import { Body } from 'js/design_system/Body';
import Badge from 'js/design_system/Badge';
import { HeadingLink } from 'js/design_system/link/HeadingLink';
import { BodyLink } from 'js/design_system/link/BodyLink';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import { CiterightContext } from './CiterightContextProvider';
import { Heading } from 'js/design_system/Heading';
import { Configuration } from './Configuration';
import { Dropdown } from 'js/design_system/forms/Dropdown';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled.div`
  border: 1px solid var(--stroke-light, #e2e8f0);
  border-radius: 8px;
  padding: 12px;
  background: var(--background-white, #ffffff);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const JobHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const JobDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CollapsibleButton = styled.button`
  appearance: none;
  padding: 8px 12px;
  margin-top: 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary, #6c757d);
  background-color: #f8f9fa;
  border: 1px solid var(--stroke-light, #e9ecef);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease-in-out;

  &:hover {
    background-color: #e9ecef;
    border-color: #dee2e6;
    color: var(--text-hover, #5a6268);
  }

  &:active {
    background-color: #dee2e6;
    border-color: #ced4da;
    transform: translateY(1px);
  }

  &:focus {
    outline: 0;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const CollapsibleContent = styled.div`
  margin-top: 8px;
  border-radius: 4px;
  border: 1px solid var(--stroke-light, #e9ecef);
  background-color: #f8f9fa;
  padding: 12px;
`;

const JsonContent = styled.pre`
  background-color: #f1f3f4;
  padding: 8px;
  border-radius: 4px;
  font-size: 11px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: var(--stroke-light, #e2e8f0);
  border-radius: 4px;
  overflow: hidden;
  margin-top: 4px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: var(--success-dark, #10b981);
  transition: all 0.3s ease;

  &.near-limit {
    background-color: var(--error-dark, #ef4444);
  }
`;

const ReportHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ReportSummary = styled.div`
  border: 1px solid var(--stroke-light, #e2e8f0);
  border-radius: 8px;
  padding: 16px;
  background: var(--background-white, #ffffff);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const ReportSummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReportActions = styled.div`
  border: 1px solid var(--stroke-light, #e2e8f0);
  border-radius: 8px;
  padding: 16px;
  background: var(--background-white, #ffffff);
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ResetButton = styled.button`
  appearance: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #000);
  background-color: #f8f9fa;
  border: 1px solid var(--stroke-light, #e2e8f0);
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background-color: #e9ecef;
    border-color: #dee2e6;
  }

  &:active {
    background-color: #dee2e6;
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const JobSection = styled.div`
  border: 1px solid var(--stroke-light, #e2e8f0);
  border-radius: 8px;
  background: var(--background-white, #ffffff);
  overflow: hidden;
`;

const JobSectionHeader = styled.button`
  width: 100%;
  appearance: none;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border: none;
  border-bottom: 1px solid var(--stroke-light, #e2e8f0);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: #e9ecef;
  }

  &:focus {
    outline: 0;
    box-shadow: inset 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const JobSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: var(--text-primary, #000);
`;

const JobSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
`;

const FilterSection = styled.div`
  border: 1px solid var(--stroke-light, #e2e8f0);
  border-radius: 8px;
  padding: 16px;
  background: var(--background-white, #ffffff);
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const getStatusColor = (
  status: string,
): { color: string; backgroundColor: string } => {
  switch (status) {
    case 'finished':
      return { color: '#155724', backgroundColor: '#d4edda' };
    case 'running':
      return { color: '#0c5460', backgroundColor: '#d1ecf1' };
    case 'enqueued':
      return { color: '#856404', backgroundColor: '#fff3cd' };
    case 'error':
      return { color: '#721c24', backgroundColor: '#f8d7da' };
    case 'dead':
      return { color: '#ffffff', backgroundColor: '#b60000' };
    default:
      return { color: '#000', backgroundColor: '#f0f0f0' };
  }
};

const getJobStatus = (job: Job): string => {
  if (job.dead_at) return 'dead';
  if (job.error || job.error_message) return 'error';
  if (job.completed_at) return 'finished';
  if (job.started_at) return 'running';
  return 'enqueued';
};

const formatTime = (timeString?: string): string => {
  if (!timeString) return '';
  const date = new Date(timeString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const calculateDuration = (
  startTime?: string,
  endTime?: string,
  now?: Date,
): string => {
  if (!startTime) return '';
  const start = new Date(startTime);
  let end = now || new Date();
  if (endTime) {
    end = new Date(endTime);
  }
  const diffMs = end.getTime() - start.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const remainingSecs = diffSecs % 60;

  if (diffMins > 0) {
    return `${diffMins}m ${remainingSecs}s`;
  }
  return `${diffSecs}s`;
};

const getRelevantTimestamp = (job: Job): Date => {
  // Priority order for timestamp: completed_at > error_at > started_at > enqueued_at
  if (job.completed_at) return new Date(job.completed_at);
  if (job.error_at) return new Date(job.error_at);
  if (job.started_at) return new Date(job.started_at);
  return new Date(job.enqueued_at);
};

const groupJobsByStatus = (jobs: Job[]) => {
  const errorJobs: Job[] = [];
  const runningJobs: Job[] = [];
  const enqueuedJobs: Job[] = [];
  const completedJobs: Job[] = [];

  jobs.forEach((job) => {
    const status = getJobStatus(job);
    if (status === 'error' || status === 'dead') {
      errorJobs.push(job);
    } else if (status === 'running') {
      runningJobs.push(job);
    } else if (status === 'enqueued') {
      enqueuedJobs.push(job);
    } else {
      completedJobs.push(job);
    }
  });

  // Sort each group by most recent first
  const sortByMostRecent = (a: Job, b: Job) => {
    const aTime = getRelevantTimestamp(a);
    const bTime = getRelevantTimestamp(b);
    return bTime.getTime() - aTime.getTime();
  };

  return {
    error: errorJobs.sort(sortByMostRecent),
    running: runningJobs.sort(sortByMostRecent),
    enqueued: enqueuedJobs.sort(sortByMostRecent),
    completed: completedJobs.sort(sortByMostRecent),
  };
};

const CostQuotaDisplay = ({ costQuota }: { costQuota: CostQuota }) => {
  const usagePercentage =
    (costQuota.daily_cost / costQuota.daily_limit_cost) * 100;
  const isNearLimit = usagePercentage > 80;

  const formatCost = (num: number) => `$${num.toFixed(2)}`;

  return (
    <Card>
      <Body>
        <strong>Cost Quota (Daily)</strong>
      </Body>
      <Row>
        <Body size="sm">Used:</Body>
        <Body size="sm">{formatCost(costQuota.daily_cost)}</Body>
      </Row>
      <Row>
        <Body size="sm">Limit:</Body>
        <Body size="sm">{formatCost(costQuota.daily_limit_cost)}</Body>
      </Row>
      <Row>
        <Body size="sm">Remaining:</Body>
        <Body size="sm">{formatCost(costQuota.remaining_cost)}</Body>
      </Row>
      <ProgressBar>
        <ProgressFill
          className={isNearLimit ? 'near-limit' : ''}
          style={{ width: `${usagePercentage}%` }}
        />
      </ProgressBar>
      <Body
        size="sm"
        style={{
          color: isNearLimit
            ? 'var(--error-dark, #ef4444)'
            : 'var(--text-secondary, #6b7280)',
        }}
      >
        {usagePercentage.toFixed(1)}% used
      </Body>
    </Card>
  );
};

const formatDuration = (seconds?: number): string => {
  if (!seconds) return 'N/A';
  if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}m ${secs}s`;
};

type JobSectionKey = 'error' | 'running' | 'enqueued' | 'completed';

export const DebugPage = () => {
  const context = useContext(CiterightContext);
  const isPolling = context?.isPolling ?? false;

  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [expandedBacktraces, setExpandedBacktraces] = useState<Set<string>>(
    new Set(),
  );
  const [expandedSections, setExpandedSections] = useState<Set<JobSectionKey>>(
    new Set(['error', 'running', 'enqueued']),
  );
  const [isResetting, setIsResetting] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [selectedClassName, setSelectedClassName] = useState<string>('all');

  // Update current time every second to keep job timings live
  useEffect(() => {
    const hasActiveJobs = context?.report?.jobs.some((job) => {
      const status = getJobStatus(job);
      return status === 'running' || status === 'enqueued';
    });

    if (!hasActiveJobs) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [context?.report?.jobs]);

  const toggleJobExpansion = (jobId: string) => {
    const newExpanded = new Set(expandedJobs);
    if (newExpanded.has(jobId)) {
      newExpanded.delete(jobId);
    } else {
      newExpanded.add(jobId);
    }
    setExpandedJobs(newExpanded);
  };

  const toggleBacktraceExpansion = (jobId: string) => {
    const newExpanded = new Set(expandedBacktraces);
    if (newExpanded.has(jobId)) {
      newExpanded.delete(jobId);
    } else {
      newExpanded.add(jobId);
    }
    setExpandedBacktraces(newExpanded);
  };

  const toggleSectionExpansion = (section: JobSectionKey) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const handleReset = async () => {
    if (!context?.report?.report_id) return;

    if (
      !confirm(
        'Are you sure you want to reset this report? This action cannot be undone.',
      )
    ) {
      return;
    }

    setIsResetting(true);
    try {
      const response = await fetchWithCsrf(
        routes.api_v0_citeright_reset_citation_report_path(),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ report_id: context?.report?.report_id }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to reset report');
      }

      // Reload the page to show the reset report
      window.location.reload();
    } catch (error) {
      console.error('Error resetting report:', error);
      alert('Failed to reset report. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const handleStart = async () => {
    if (!context?.report?.report_id) return;

    if (
      !confirm(
        'Are you sure you want to start this report? This will begin processing.',
      )
    ) {
      return;
    }

    setIsStarting(true);
    try {
      const response = await fetchWithCsrf(
        routes.api_v0_citeright_start_citation_report_path(),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ report_id: context?.report?.report_id }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to start report');
      }

      // Reload the page to show the started report
      window.location.reload();
    } catch (error) {
      console.error('Error starting report:', error);
      alert('Failed to start report. Please try again.');
    } finally {
      setIsStarting(false);
    }
  };

  const hasJobData = (job: Job): boolean => {
    return !!(job.args || job.result);
  };

  const renderJobCard = (job: Job) => {
    const status = getJobStatus(job);
    const enqueuedTime = formatTime(job.enqueued_at);
    const timeInQueue = calculateDuration(
      job.enqueued_at,
      job.started_at,
      currentTime,
    );
    const runningTime = calculateDuration(
      job.started_at,
      job.completed_at,
      currentTime,
    );
    const isExpanded = expandedJobs.has(job.job_id);

    const statusColors = getStatusColor(status);

    return (
      <Card key={job.job_id}>
        <JobHeader>
          <Body>
            <strong>{job.class_name}</strong>
          </Body>
          <Badge
            text={status}
            color={statusColors.color}
            backgroundColor={statusColors.backgroundColor}
          />
        </JobHeader>
        <JobDetails>
          {job.dead_at && (
            <Body size="sm" style={{ color: 'var(--error-dark, #721c24)' }}>
              Dead at: {formatTime(job.dead_at)}
            </Body>
          )}
          {enqueuedTime && <Body size="sm">Enqueued {enqueuedTime}</Body>}
          {timeInQueue && <Body size="sm">Time in queue: {timeInQueue}</Body>}
          {runningTime && <Body size="sm">Running time: {runningTime}</Body>}
          {job.trace_id && (
            <Body size="sm">
              Trace ID:{' '}
              <BodyLink
                href={`https://app.datadoghq.com/apm/trace/${job.trace_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {job.trace_id}
              </BodyLink>
            </Body>
          )}
          {job.prompt_trace_id && (
            <Body size="sm">
              Prompt Trace ID:{' '}
              <BodyLink
                href={`/citeright/trace/${job.prompt_trace_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {job.prompt_trace_id}
              </BodyLink>
            </Body>
          )}
          {job.error && (
            <Body size="sm" style={{ color: 'var(--error-dark, #721c24)' }}>
              Error: {job.error}
            </Body>
          )}
          {job.error_message && (
            <Body size="sm" style={{ color: 'var(--error-dark, #721c24)' }}>
              Error Message: {job.error_message}
            </Body>
          )}
          {job.error_count && (
            <Body size="sm" style={{ color: 'var(--error-dark, #721c24)' }}>
              Error count: {job.error_count}
            </Body>
          )}
          {job.error_backtrace && job.error_backtrace.length > 0 && (
            <>
              <CollapsibleButton
                onClick={() => toggleBacktraceExpansion(job.job_id)}
                type="button"
                style={{ marginTop: '8px' }}
              >
                {expandedBacktraces.has(job.job_id) ? '▼' : '▶'} Show Stack
                Trace ({job.error_backtrace.length} lines)
              </CollapsibleButton>
              {expandedBacktraces.has(job.job_id) && (
                <CollapsibleContent>
                  <Body
                    size="sm"
                    style={{ fontWeight: 'bold', marginBottom: '4px' }}
                  >
                    Stack Trace:
                  </Body>
                  <JsonContent>{job.error_backtrace.join('\n')}</JsonContent>
                </CollapsibleContent>
              )}
            </>
          )}
        </JobDetails>

        {hasJobData(job) && (
          <>
            <CollapsibleButton
              onClick={() => toggleJobExpansion(job.job_id)}
              type="button"
            >
              {isExpanded ? '▼' : '▶'} Show {job.args ? 'Args' : ''}
              {job.args && job.result ? ' & ' : ''}
              {job.result ? 'Result' : ''}
            </CollapsibleButton>
            {isExpanded && (
              <CollapsibleContent>
                {job.args != null && (
                  <div>
                    <Body
                      size="sm"
                      style={{ fontWeight: 'bold', marginBottom: '4px' }}
                    >
                      Arguments:
                    </Body>
                    <JsonContent>
                      {JSON.stringify(job.args, null, 2)}
                    </JsonContent>
                  </div>
                )}
                {job.result != null && (
                  <div style={{ marginTop: job.args ? '12px' : '0' }}>
                    <Body
                      size="sm"
                      style={{ fontWeight: 'bold', marginBottom: '4px' }}
                    >
                      Result:
                    </Body>
                    <JsonContent>
                      {JSON.stringify(job.result, null, 2)}
                    </JsonContent>
                  </div>
                )}
              </CollapsibleContent>
            )}
          </>
        )}
      </Card>
    );
  };

  const renderJobSection = (
    title: string,
    jobs: Job[],
    sectionKey: JobSectionKey,
  ) => {
    if (jobs.length === 0) return null;

    const isExpanded = expandedSections.has(sectionKey);

    return (
      <JobSection key={sectionKey}>
        <JobSectionHeader
          onClick={() => toggleSectionExpansion(sectionKey)}
          type="button"
        >
          <JobSectionTitle>
            {isExpanded ? '▼' : '▶'} {title} ({jobs.length})
          </JobSectionTitle>
        </JobSectionHeader>
        {isExpanded && (
          <JobSectionContent>{jobs.map(renderJobCard)}</JobSectionContent>
        )}
      </JobSection>
    );
  };

  // Extract unique class names from all jobs
  const uniqueClassNames = Array.from(
    new Set((context?.report?.jobs ?? []).map((job) => job.class_name)),
  ).sort();

  // Filter jobs based on selected class name
  const filteredJobs =
    selectedClassName === 'all'
      ? (context?.report?.jobs ?? [])
      : (context?.report?.jobs ?? []).filter(
          (job) => job.class_name === selectedClassName,
        );

  const groupedJobs = groupJobsByStatus(filteredJobs);
  const timingMetadata = context?.report?.timing_metadata;

  return (
    <Container>
      <Heading size="lg" as="h1">
        Debug - Report {context?.report?.report_id || 'N/A'}
      </Heading>
      {context?.report?.report_id && (
        <ReportHeader>
          <HeadingLink
            href={`/v0/citeright/citation_report?subdomain_param=api&report_id=${context?.report?.report_id}`}
          >
            Citation Report {context?.report?.report_id}
          </HeadingLink>
          <Badge
            text={isPolling ? 'Polling Active' : 'Polling Stopped'}
            color={isPolling ? '#0c5460' : '#6c757d'}
            backgroundColor={isPolling ? '#d1ecf1' : '#e9ecef'}
          />
        </ReportHeader>
      )}
      {timingMetadata && (
        <ReportSummary>
          <ReportSummaryItem>
            <Body size="sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>
              Time to first claim
            </Body>
            <Body style={{ fontWeight: 'bold' }}>
              {formatDuration(timingMetadata.time_to_first_claim)}
            </Body>
          </ReportSummaryItem>
          <ReportSummaryItem>
            <Body size="sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>
              Time to first claim complete
            </Body>
            <Body style={{ fontWeight: 'bold' }}>
              {formatDuration(timingMetadata.time_to_first_claim_complete)}
            </Body>
          </ReportSummaryItem>
          <ReportSummaryItem>
            <Body size="sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>
              Time to first suggested work
            </Body>
            <Body style={{ fontWeight: 'bold' }}>
              {formatDuration(timingMetadata.time_to_first_suggested_work)}
            </Body>
          </ReportSummaryItem>
          <ReportSummaryItem>
            <Body size="sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>
              Time to first reference
            </Body>
            <Body style={{ fontWeight: 'bold' }}>
              {formatDuration(timingMetadata.time_to_first_reference)}
            </Body>
          </ReportSummaryItem>
          <ReportSummaryItem>
            <Body size="sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>
              Claims found
            </Body>
            <Body style={{ fontWeight: 'bold' }}>
              {timingMetadata.claims_found}
            </Body>
          </ReportSummaryItem>
          <ReportSummaryItem>
            <Body size="sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>
              Suggested works found
            </Body>
            <Body style={{ fontWeight: 'bold' }}>
              {timingMetadata.suggested_works_found}
            </Body>
          </ReportSummaryItem>
          <ReportSummaryItem>
            <Body size="sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>
              References found
            </Body>
            <Body style={{ fontWeight: 'bold' }}>
              {timingMetadata.references_found}
            </Body>
          </ReportSummaryItem>
        </ReportSummary>
      )}
      {context?.report?.cost_quota && (
        <CostQuotaDisplay costQuota={context?.report?.cost_quota} />
      )}
      {context?.report?.configuration && (
        <Configuration
          configuration={context?.report?.configuration}
          reportId={context?.report?.report_id}
        />
      )}
      {context?.report && (
        <ReportActions>
          <Body size="sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>
            Debug Actions
          </Body>
          <ResetButton
            onClick={handleStart}
            disabled={isStarting || (context?.report?.jobs?.length ?? 0) > 0}
          >
            {isStarting ? 'Starting...' : 'Start Report'}
          </ResetButton>
          <ResetButton onClick={handleReset} disabled={isResetting}>
            {isResetting ? 'Resetting...' : 'Reset Report'}
          </ResetButton>
        </ReportActions>
      )}
      {uniqueClassNames.length > 0 && (
        <FilterSection>
          <Dropdown
            label="Filter by Job Class"
            value={selectedClassName}
            options={['all', ...uniqueClassNames]}
            renderOption={(option) => (option === 'all' ? 'All Jobs' : option)}
            renderValue={(value) => (value === 'all' ? 'All Jobs' : value)}
            onChange={setSelectedClassName}
            style={{ minWidth: '300px' }}
          />
        </FilterSection>
      )}
      {renderJobSection('Errors', groupedJobs.error, 'error')}
      {renderJobSection('Running', groupedJobs.running, 'running')}
      {renderJobSection('Enqueued', groupedJobs.enqueued, 'enqueued')}
      {renderJobSection('Completed', groupedJobs.completed, 'completed')}
    </Container>
  );
};
